// #tag: RESULT DETAIL SCREEN
// #purpose: Shows detailed scorecard metrics, qualification cutoffs, merit lists, official portal links, and AI exam summarization.
import React, { useState } from 'react';
import { ArrowLeft, ExternalLink, Sparkles } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import { fetchAiStatus, summarizeExam } from '../api/ai';
import { isAiFeatureAvailable } from '../lib/aiStatus';
import { useResult } from '../hooks/useExamData';
import { safeExternalUrl } from '../lib/safeExternalUrl';

export function ResultDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: result, isLoading, isError, refetch } = useResult(id);
  const [aiSummary, setAiSummary] = useState<string[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: fetchAiStatus,
    staleTime: 60_000,
    retry: 2,
  });

  const aiAvailable = isAiFeatureAvailable(aiStatus);

  if (isLoading) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading result…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load this result." onRetry={() => refetch()} />
      </section>
    );
  }

  if (!result) {
    return (
      <section className="adaptive-page">
        <EmptyState
          title="Result not found"
          message="This result may have been removed or the link is incorrect."
          illustrationContext="empty.results"
          action={
            <button type="button" className="primary-action" onClick={() => navigate('/results')}>
              Back to results
            </button>
          }
        />
      </section>
    );
  }

  const officialWebsite = safeExternalUrl(result.officialWebsite);
  const resultLink = safeExternalUrl(result.resultUrl);

  const handleAiSummary = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await summarizeExam({
        title: result.title,
        organization: result.organization,
        examCategory: result.examCategory,
        officialNotice: result.officialNotice,
        importantDates: result.importantDates,
      });
      setAiSummary(response.summary);
    } catch (err: unknown) {
      setAiError(err instanceof Error ? err.message : 'Could not generate summary.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <section className="adaptive-page">
      <button type="button" className="text-action wfx-back-link" onClick={() => navigate('/results')}>
        <ArrowLeft size={16} /> Back to results
      </button>

      <span className="section-kicker">{result.examCategory}</span>
      <h1>{result.title}</h1>
      <p className="wfx-lead">{result.organization}</p>

      <div className="wfx-detail-grid">
        {aiAvailable && (
          <article className="glass-surface wfx-detail-card wfx-ai-summary-card">
            <div className="wfx-ai-summary-head">
              <h2>AI summary</h2>
              <button
                type="button"
                className="secondary-action compact"
                onClick={handleAiSummary}
                disabled={aiLoading}
              >
                <Sparkles size={14} aria-hidden="true" />
                {aiLoading ? 'Summarizing…' : aiSummary ? 'Refresh summary' : 'Summarize this notice'}
              </button>
            </div>
            {aiError && <div className="auth-error" role="alert">{aiError}</div>}
            {aiSummary ? (
              <ul className="wfx-ai-summary-list">
                {aiSummary.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="wfx-muted">
                Get a plain-English breakdown of what this recruitment or result notice means and what to do next.
              </p>
            )}
          </article>
        )}

        <article className="glass-surface wfx-detail-card">
          <h2>Official notice</h2>
          <p>
            {result.officialNotice ||
              `The ${result.organization} has declared the result for ${result.title}. Candidates can check their scores using their registration credentials on the official website.`}
          </p>
          {officialWebsite && (
            <a href={officialWebsite} className="primary-action compact" target="_blank" rel="noopener noreferrer">
              Visit official website <ExternalLink size={14} />
            </a>
          )}
        </article>

        {result.importantDates && result.importantDates.length > 0 && (
          <article className="glass-surface wfx-detail-card">
            <h2>Important dates</h2>
            <ul className="wfx-date-list">
              {result.importantDates.map((d) => (
                <li key={d.label}>
                  <strong>{d.label}</strong>
                  <span>{d.date}</span>
                </li>
              ))}
            </ul>
          </article>
        )}

        <article className="glass-surface wfx-detail-card">
          <h2>Downloads & links</h2>
          <div className="wfx-link-grid">
            {resultLink && (
              <a href={resultLink} className="secondary-action" target="_blank" rel="noopener noreferrer">
                Direct result link
              </a>
            )}
            {result.hasScoreCard && <button type="button" className="secondary-action">Score card download</button>}
            {result.hasMeritList && <button type="button" className="secondary-action">Merit list</button>}
            {result.hasCutoff && <button type="button" className="secondary-action">Cutoff details</button>}
            {result.hasCounselling && <button type="button" className="secondary-action">Counselling details</button>}
          </div>
        </article>

        <article className="glass-surface wfx-detail-card wfx-vault-cta">
          <h2>Attach from Student Vault</h2>
          <p>Need documents for DV or counselling? Pick them directly from your vault — no re-uploading.</p>
          <button type="button" className="primary-action" onClick={() => navigate('/vault')}>
            Open Student Vault
          </button>
        </article>
      </div>
    </section>
  );
}
